import type { Me } from "@contract/firm/me";
import * as React from "react";
import { fetchMe } from "./me";

type MeState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; me: Me };

type MeContextValue = { state: MeState; reload: () => void };

const MeContext = React.createContext<MeContextValue | null>(null);

/** Loads /v1/me once per signed-in session and shares it with every panel page. */
export function MeProvider({ children, load = fetchMe }: { children: React.ReactNode; load?: () => Promise<Me> }) {
  const [state, setState] = React.useState<MeState>({ status: "loading" });
  const [nonce, setNonce] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    load()
      .then((me) => alive && setState({ status: "ready", me }))
      .catch((error: unknown) =>
        alive && setState({ status: "error", message: error instanceof Error ? error.message : "Couldn't load your account." }),
      );
    return () => {
      alive = false;
    };
  }, [load, nonce]);
  const value = React.useMemo(() => ({ state, reload: () => setNonce((n) => n + 1) }), [state]);
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const value = React.useContext(MeContext);
  if (!value) throw new Error("useMe outside MeProvider");
  return value;
}
